// 画像 / drawio 図のクリック選択 + 角ハンドルでのドラッグリサイズ
// + サイズプリセット (小/中/大/原寸) フローティングツールバー。
// editor surface に attach し、`figure[data-lexical-image-node]`
// と `figure[data-lexical-drawio-node]` を対象にする。
//
// 動作:
//   - figure をクリック → `is-selected` 付与、ハンドル(画像のみ) + サイズツールバー表示
//   - 外をクリック → 解除
//   - 角ハンドル drag (画像のみ) → mouseup で ImageNode.setWidth(px)
//   - サイズボタン → コラム幅に対する割合で width(px) を設定。原寸 = width クリア

import { $getNodeByKey } from "lexical"

const SELECTED_CLASS = "is-selected"
const MIN_WIDTH = 80
const FIG_SELECTOR = "figure[data-lexical-image-node], figure[data-lexical-drawio-node]"
const SIZE_PRESETS = [
  { label: "小",   ratio: 0.25 },
  { label: "中",   ratio: 0.5 },
  { label: "大",   ratio: 0.75 },
  { label: "原寸", ratio: null },
]

function findImageKey(editor, figure) {
  let foundKey = null
  editor.getEditorState().read(() => {
    const map = editor.getEditorState()._nodeMap
    for (const [key, _node] of map) {
      if (editor.getElementByKey(key) === figure) { foundKey = key; break }
    }
  })
  return foundKey
}

function ownerFigure(el) {
  return el ? el.closest(FIG_SELECTOR) : null
}

// 画像 figure (ハンドルを持つ) のみ
function ownerImageFigure(el) {
  return el ? el.closest("figure[data-lexical-image-node]") : null
}

function aspectRatio(figure) {
  const img = figure.querySelector("img")
  if (!img || !img.naturalWidth || !img.naturalHeight) return null
  return img.naturalHeight / img.naturalWidth
}

// surface の内容幅 (padding 控除) をプリセット計算の基準にする
function contentWidth(surface) {
  const cs = getComputedStyle(surface)
  const pad = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0)
  return Math.max(120, surface.clientWidth - pad)
}

export function attachImageResize(editor, surface) {
  let activeDrag = null
  let selectedFigure = null

  // ─── サイズツールバー (一度だけ生成、body に置いて絶対配置) ───
  const toolbar = document.createElement("div")
  toolbar.className = "akebono-image-size-toolbar"
  toolbar.style.display = "none"
  for (const p of SIZE_PRESETS) {
    const b = document.createElement("button")
    b.type = "button"
    b.textContent = p.label
    b.dataset.ratio = p.ratio == null ? "" : String(p.ratio)
    toolbar.appendChild(b)
  }
  document.body.appendChild(toolbar)

  const positionToolbar = () => {
    if (!selectedFigure) return
    const r = selectedFigure.getBoundingClientRect()
    toolbar.style.top  = `${window.scrollY + r.top - toolbar.offsetHeight - 8}px`
    toolbar.style.left = `${window.scrollX + r.left}px`
  }
  const showToolbar = (fig) => {
    selectedFigure = fig
    toolbar.style.display = "flex"
    positionToolbar()
  }
  const hideToolbar = () => {
    selectedFigure = null
    toolbar.style.display = "none"
  }

  const clearSelection = () => {
    surface.querySelectorAll(`${FIG_SELECTOR}`).forEach(f => f.classList.remove(SELECTED_CLASS))
    hideToolbar()
  }

  // ─── 選択 ───────────────────────────────────────────
  const onSurfaceMouseDown = (e) => {
    if (e.target.dataset?.handle) return            // ハンドル drag は別処理
    if (e.target.closest("[data-drawio-edit]")) return // 編集ボタンは選択せず click に委ねる
    const fig = ownerFigure(e.target)
    if (fig) {
      clearSelection()
      fig.classList.add(SELECTED_CLASS)
      showToolbar(fig)
      e.preventDefault()
    } else {
      clearSelection()
    }
  }
  surface.addEventListener("mousedown", onSurfaceMouseDown)

  const onDocMouseDown = (e) => {
    if (!surface.contains(e.target) && !toolbar.contains(e.target)) clearSelection()
  }
  document.addEventListener("mousedown", onDocMouseDown)

  // 選択中はスクロール/リサイズでツールバー位置を追従
  const onReposition = () => { if (selectedFigure) positionToolbar() }
  window.addEventListener("scroll", onReposition, true)
  window.addEventListener("resize", onReposition)

  // ─── サイズプリセットボタン ─────────────────────────
  const onToolbarClick = (e) => {
    const btn = e.target.closest("button")
    if (!btn || !selectedFigure) return
    e.preventDefault()
    const ratioStr = btn.dataset.ratio
    const width = ratioStr === "" ? null : Math.round(contentWidth(surface) * parseFloat(ratioStr))
    const key = findImageKey(editor, selectedFigure)
    if (key) {
      editor.update(() => {
        const node = $getNodeByKey(key)
        if (node && typeof node.setWidth === "function") node.setWidth(width)
      })
    }
    requestAnimationFrame(positionToolbar)
  }
  toolbar.addEventListener("click", onToolbarClick)

  // ─── ハンドル drag (画像のみ) ───────────────────────
  const onHandleMouseDown = (e) => {
    const handle = e.target.closest(".akebono-image-handle")
    if (!handle) return
    const figure = ownerImageFigure(handle)
    if (!figure) return

    e.preventDefault()
    e.stopPropagation()

    const img = figure.querySelector("img")
    const rect = img.getBoundingClientRect()
    const corner = handle.dataset.handle
    const direction = (corner === "nw" || corner === "sw") ? -1 : 1
    const key = findImageKey(editor, figure)

    activeDrag = {
      figure, img, key,
      startX: e.clientX,
      startWidth: rect.width,
      direction,
      ratio: aspectRatio(figure),
      maxWidth: surface.clientWidth - 16,
    }
    figure.classList.add(SELECTED_CLASS, "is-resizing")
    document.addEventListener("mousemove", onMouseMove)
    document.addEventListener("mouseup", onMouseUp)
    document.body.style.cursor = "ew-resize"
    document.body.style.userSelect = "none"
  }
  surface.addEventListener("mousedown", onHandleMouseDown)

  const onMouseMove = (e) => {
    if (!activeDrag) return
    const dx = (e.clientX - activeDrag.startX) * activeDrag.direction
    let next = activeDrag.startWidth + dx
    next = Math.max(MIN_WIDTH, Math.min(activeDrag.maxWidth, next))
    activeDrag.img.style.width  = `${next}px`
    activeDrag.img.style.height = "auto"
  }

  const onMouseUp = () => {
    if (!activeDrag) return
    const { img, key, figure } = activeDrag
    const finalW = Math.round(parseFloat(img.style.width) || img.getBoundingClientRect().width)

    document.removeEventListener("mousemove", onMouseMove)
    document.removeEventListener("mouseup", onMouseUp)
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    figure.classList.remove("is-resizing")

    if (key) {
      editor.update(() => {
        const node = $getNodeByKey(key)
        if (node && typeof node.setWidth === "function") node.setWidth(finalW)
      })
    }
    img.style.width  = ""
    img.style.height = ""
    activeDrag = null
    requestAnimationFrame(positionToolbar)
  }

  return () => {
    surface.removeEventListener("mousedown", onSurfaceMouseDown)
    surface.removeEventListener("mousedown", onHandleMouseDown)
    document.removeEventListener("mousedown", onDocMouseDown)
    document.removeEventListener("mousemove", onMouseMove)
    document.removeEventListener("mouseup", onMouseUp)
    window.removeEventListener("scroll", onReposition, true)
    window.removeEventListener("resize", onReposition)
    toolbar.removeEventListener("click", onToolbarClick)
    toolbar.remove()
  }
}
